import { useCallback, useEffect, useRef, useState } from "react";
import type { PictureVideoStatus } from "@studio/shared";
import { ApiError, apiRequest } from "../lib/api";
import { subscribePendingGifts } from "../lib/gifts";
import { videoFailureMessage } from "../lib/videoFailure";
import { InlineSpinner } from "./InlineSpinner";

/**
 * 그림 → AI 영상(2026-10-16 아름학교 · jery 결정 1~3).
 *
 * 그림 한 장을 5초짜리 AI 영상으로 만든다 — 포스터 「칸 그림 → AI 영상」(D11)과 같은 길이다.
 * 🔑 학생당 3편(+ 선생님 선물). 다 쓰면 패널은 남기고 「다 썼어요」 를 보여 준다.
 * 🔑 영상은 «지금 그림» 으로 만든 것만 보인다 — 그림을 다시 그리면 서버가 옛 영상을 숨긴다.
 * 🔑 옛 그림 수업방(2026-10-16 전 · 한도 0)은 패널을 아예 그리지 않는다.
 */

const POLL_MS = 5_000;
const ACTIVE = new Set(["queued", "dispatched", "cancel_requested"]);
const PROMPT_MAX = 100;

export interface PictureVideo {
  status: PictureVideoStatus | null;
  active: boolean;
  refresh: () => Promise<void>;
  /** POST 가 돌려준 작업 id 로 «만드는 중» 을 먼저 그린다 — 직후 조회가 실패해도 폴링이 돈다. */
  started: (jobId: string) => void;
}

/**
 * 지금 그림의 영상 상태. 만드는 중이면 5초마다 다시 읽는다. `imageAssetId` 가 바뀌면(다시 그리면) 새로 읽는다.
 * 🔑 Codex 검수(2026-10-16) 반영:
 *    - 늦게 온 옛 응답(다시 그리기 전 그림)은 버린다 — 요청마다 세대 번호를 붙인다.
 *    - 한 번 못 읽어도 이전 상태를 지우지 않는다(패널이 사라지고 다시 읽기가 멈추던 것).
 *    - 선생님이 AI 영상을 선물하면 다시 읽는다(남은 횟수 0 → 버튼이 다시 보이게).
 */
export function usePictureVideo(workId: string, imageAssetId: string | null): PictureVideo {
  const [status, setStatus] = useState<PictureVideoStatus | null>(null);
  // 🔑 마지막 조회가 실패했는가(부검 2026-10-26). 실패한 동안은 다시 읽기를 멈추지 않는다 —
  //    종전에는 첫 조회가 실패하면 status 가 null 로 남아 패널도 폴링도 영영 안 떴다.
  const [readFailed, setReadFailed] = useState(false);
  const generation = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    // 🔑 요청마다 번호를 올린다 — 같은 그림의 요청이 겹쳐도 «가장 나중에 보낸 것» 의 응답만 쓴다(Codex 재검수).
    const mine = ++generation.current;
    try {
      const next = await apiRequest<PictureVideoStatus>(`/api/works/${encodeURIComponent(workId)}/picture/video`);
      if (mine === generation.current) {
        setStatus(next);
        setReadFailed(false);
      }
    } catch {
      // 이전 상태를 그대로 둔다 — 아래 폴링이 다음 주기에 다시 읽는다.
      if (mine === generation.current) setReadFailed(true);
    }
  }, [workId]);

  useEffect(() => {
    generation.current += 1;
    setStatus(null);
    setReadFailed(false);
    if (imageAssetId) void refresh();
    return () => {
      generation.current += 1;
    };
  }, [imageAssetId, refresh]);

  useEffect(() => subscribePendingGifts(() => void refresh()), [refresh]);

  const active = Boolean(status?.job && ACTIVE.has(status.job.state));
  const polling = active || (readFailed && imageAssetId !== null);
  useEffect(() => {
    if (!polling) return undefined;
    // 🔑 앞 조회가 끝난 뒤에 다음 조회를 건다(Codex 검수) — 고정 간격으로 겹쳐 보내면, 응답이 5초보다 느릴 때
    //    늘 더 나중 요청이 앞 응답을 «옛것» 으로 버려 readFailed 가 영영 안 풀렸다.
    let stopped = false;
    let timer: number | undefined;
    const tick = async (): Promise<void> => {
      await refresh();
      if (!stopped) timer = window.setTimeout(() => void tick(), POLL_MS);
    };
    timer = window.setTimeout(() => void tick(), POLL_MS);
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [polling, refresh]);

  const started = useCallback((jobId: string): void => {
    setStatus((current) => current ? { ...current, job: { id: jobId, state: "queued", error_code: null } } : current);
  }, []);

  return { status, active, refresh, started };
}

function startError(caught: unknown): string {
  if (!(caught instanceof ApiError)) return "영상을 시작하지 못했어요. 다시 눌러 주세요.";
  if (caught.code === "LIMIT_EXCEEDED") return "AI 영상 횟수를 다 썼어요. 선생님께 요청해 주세요.";
  if (caught.code === "BUDGET_EXHAUSTED") return "오늘 만들 수 있는 AI 영상을 다 썼어요.";
  return caught.message;
}

export function PictureVideoPanel({
  workId,
  video,
  disabled = false,
}: {
  workId: string;
  video: PictureVideo;
  disabled?: boolean;
}) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { status, active } = video;
  if (!status?.enabled) return null;

  const failed = status.job?.state === "failed" || status.job?.state === "outcome_unknown";
  const start = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const reply = await apiRequest<{ job_id?: string }>(`/api/works/${encodeURIComponent(workId)}/picture/video`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(prompt.trim() ? { prompt: prompt.trim() } : {}),
      });
      if (reply?.job_id) video.started(reply.job_id);
      await video.refresh();
    } catch (caught) {
      setError(startError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pic-video-panel" aria-labelledby="pic-video-title" aria-live="polite">
      <h2 id="pic-video-title">그림을 움직이는 영상으로</h2>
      {status.from_upload ? (
        <p>내가 올린 그림은 AI 영상으로 만들 수 없어요. AI 로 그린 그림만 돼요.</p>
      ) : active ? (
        <p className="pic-video-progress"><InlineSpinner label="영상을 만들고 있어요 · 1~2분쯤 걸려요" /></p>
      ) : (
        <>
          <p>
            {status.clip_url ? "영상이 나왔어요! 다른 움직임으로 다시 만들 수도 있어요." : "내 그림이 5초 동안 움직여요."}
            {" "}<b>남은 횟수 {status.remaining}번</b>
          </p>
          {failed ? (
            <p className="pic-video-failed">{videoFailureMessage(status.job?.error_code)} 횟수는 줄지 않았어요.</p>
          ) : null}
          {status.remaining > 0 ? (
            <div className="pic-video-form">
              <label htmlFor="pic-video-prompt">어떻게 움직일까요? (안 써도 돼요)</label>
              <input
                id="pic-video-prompt"
                value={prompt}
                maxLength={PROMPT_MAX}
                placeholder="예: 머리카락이 바람에 날려요"
                onChange={(event) => setPrompt(event.target.value)}
              />
              <button className="button-primary" type="button" disabled={disabled || busy} onClick={() => void start()}>
                {busy ? <InlineSpinner label="시작하는 중..." /> : status.clip_url ? "다시 만들기" : "영상으로 만들기"}
              </button>
            </div>
          ) : (
            <p>AI 영상 횟수를 다 썼어요. 더 만들고 싶으면 선생님께 요청해 주세요.</p>
          )}
        </>
      )}
      {error ? <p className="pic-video-failed" role="alert">{error}</p> : null}
    </section>
  );
}
